// F8: text extraction runs entirely on the user's device. PDFs use pdf.js; scanned
// PDFs (no text layer) fall back to Tesseract OCR; DOCX is unzipped and its XML read.
import { unzipSync } from "fflate";
import { reportError } from "./report";

export type ExtractMethod = "pdf" | "ocr" | "docx";

export interface ExtractResult {
  text: string;
  method: ExtractMethod;
  pages?: number;
  /** Shown to the user when extraction was partial. */
  note?: string;
}

export type Progress = (message: string) => void;

export const MAX_OCR_PAGES = 10;
/** Below this many characters per page a PDF is treated as a scan. */
const SCANNED_CHARS_PER_PAGE = 40;
const MAX_DOCX_XML_BYTES = 50 * 1024 * 1024;

export function kindOf(file: { name: string }): "pdf" | "docx" | null {
  const dot = file.name.lastIndexOf(".");
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : "";
  return ext === "pdf" || ext === "docx" ? ext : null;
}

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

export function decodeXmlEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (m, e: string) => {
    if (e[0] === "#") {
      const code = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : "";
    }
    return ENTITIES[e.toLowerCase()] ?? m;
  });
}

/** WordprocessingML → plain text: paragraphs become lines, tabs and breaks are kept. */
export function docxXmlToText(xml: string): string {
  return decodeXmlEntities(
    xml
      .replace(/<w:tab\/>/g, "\t")
      .replace(/<w:(br|cr)\/>/g, "\n")
      .replace(/<\/w:p>/g, "\n")
      .replace(/<[^>]+>/g, ""),
  )
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function extractDocx(bytes: Uint8Array): ExtractResult {
  let files;
  try {
    files = unzipSync(bytes, {
      // Only the main document, and only if it isn't a zip bomb.
      filter: (f) => f.name === "word/document.xml" && f.originalSize <= MAX_DOCX_XML_BYTES,
    });
  } catch {
    throw new Error("This Word file looks damaged. Try opening it in Word and saving it again.");
  }
  const xml = files["word/document.xml"];
  if (!xml) throw new Error("This doesn't look like a Word document.");
  return { text: docxXmlToText(new TextDecoder().decode(xml)), method: "docx" };
}

interface PdfTextItem {
  str?: string;
  hasEOL?: boolean;
}

export function pdfItemsToText(items: readonly PdfTextItem[]): string {
  let out = "";
  for (const item of items) {
    if (typeof item.str !== "string") continue;
    out += item.str;
    out += item.hasEOL ? "\n" : item.str && !item.str.endsWith(" ") ? " " : "";
  }
  return out.replace(/[ \t]+\n/g, "\n").trim();
}

async function loadPdfjs() {
  const pdfjs = await import("pdfjs-dist");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  }
  return pdfjs;
}

export async function extractPdf(bytes: Uint8Array, progress: Progress = () => {}): Promise<ExtractResult> {
  const pdfjs = await loadPdfjs();
  let doc;
  try {
    doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  } catch (err) {
    const name = (err as { name?: string })?.name;
    if (name === "PasswordException") throw new Error("This PDF is password-protected, so we can't read its text. You can still upload it.");
    throw new Error("This PDF looks damaged, so we couldn't read its text. You can still upload it.");
  }
  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    progress(`Reading page ${i} of ${doc.numPages}…`);
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    pages.push(pdfItemsToText(content.items as PdfTextItem[]));
  }
  const text = pages.join("\n\n");
  if (text.replace(/\s/g, "").length >= SCANNED_CHARS_PER_PAGE * Math.min(doc.numPages, 3)) {
    return { text, method: "pdf", pages: doc.numPages };
  }

  // Probably a scan: OCR the first pages.
  const ocrPages = Math.min(doc.numPages, MAX_OCR_PAGES);
  const { createWorker } = await import("tesseract.js");
  progress("This looks like a scan. Recognising text (the first time downloads about 15 MB)…");
  const worker = await createWorker("eng");
  const ocr: string[] = [];
  try {
    for (let i = 1; i <= ocrPages; i++) {
      progress(`Recognising text on page ${i} of ${ocrPages}…`);
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvas, viewport }).promise;
      const { data } = await worker.recognize(canvas);
      ocr.push(data.text.trim());
    }
  } finally {
    await worker.terminate();
  }
  return {
    text: ocr.join("\n\n"),
    method: "ocr",
    pages: doc.numPages,
    note: doc.numPages > ocrPages ? `Only the first ${ocrPages} pages were scanned for text.` : undefined,
  };
}

export async function extractText(file: File, progress?: Progress): Promise<ExtractResult> {
  const kind = kindOf(file);
  const started = Date.now();
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (kind === "docx") return await extractDocx(bytes);
    if (kind === "pdf") return await extractPdf(bytes, progress);
    throw new Error("Only PDF and Word (.docx) files can be read.");
  } catch (err) {
    // Usually a damaged or password-protected file (a "notice"); anything else is a bug worth seeing.
    const expected = err instanceof Error && /damaged|password|doesn't look like|Only PDF/.test(err.message);
    reportError("extract", err, { kind: kind ?? "unknown", sizeBytes: file.size, ms: Date.now() - started }, expected ? "notice" : "error");
    throw err;
  }
}
